// -----------------------------------------------------------------------------
// Platform admin API — DTOs, fetchers, and query keys.
// Mirrors src/DocuSense.Application/Dtos/Admin/*.cs
// -----------------------------------------------------------------------------

import { ADMIN_AUTH_EXPIRED_EVENT, AdminTokenStore } from './auth/AdminTokenStore';

// ---------- Base fetch -------------------------------------------------------

function resolveBaseUrl(): string {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_API_BASE_URL?.trim() ?? '';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

export async function adminFetch<T>(
  path: string,
  options: { method?: string; json?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = AdminTokenStore.get();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token !== null) headers['Authorization'] = `Bearer ${token.accessToken}`;

  let body: string | undefined;
  if (options.json !== undefined) {
    body = JSON.stringify(options.json);
    headers['Content-Type'] = 'application/json';
  }

  const base = resolveBaseUrl();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
      ...(body !== undefined ? { body } : {}),
    });
  } catch (cause) {
    throw new Error(`Network error: ${String(cause)}`);
  }

  if (response.ok) {
    if (response.status === 204) return null as T;
    return response.json() as Promise<T>;
  }

  if (response.status === 401) {
    AdminTokenStore.clear();
    window.dispatchEvent(new CustomEvent(ADMIN_AUTH_EXPIRED_EVENT));
  }

  let msg = `HTTP ${response.status}`;
  try {
    const err = (await response.json()) as Record<string, unknown>;
    const detail = err['detail'] ?? err['title'] ?? err['message'];
    if (typeof detail === 'string') msg = detail;
  } catch { /* */ }
  throw new Error(msg);
}

// ---------- Enums (mirrors C# Domain enums) ----------------------------------

export const TenantStatuses = {
  Active: 'Active',
  Suspended: 'Suspended',
  Blocked: 'Blocked',
  Pending: 'Pending',
} as const;
export type TenantStatus = (typeof TenantStatuses)[keyof typeof TenantStatuses];

export const LicenseTypes = {
  Starter: 'Starter',
  Professional: 'Professional',
  Enterprise: 'Enterprise',
} as const;
/** Arbitrary plan id string — widened from the legacy 3-value enum. */
export type AdminLicenseType = string;

export const ProviderTypes = {
  OpenAiCloud: 'OpenAiCloud',
  AzureOpenAi: 'AzureOpenAi',
  AnthropicCloud: 'AnthropicCloud',
  ContentUnderstanding: 'ContentUnderstanding',
  OllamaLocal: 'OllamaLocal',
  OllamaViaAzureRelay: 'OllamaViaAzureRelay',
} as const;
export type ProviderType = (typeof ProviderTypes)[keyof typeof ProviderTypes];

// ---------- Tenant DTOs ------------------------------------------------------

export interface TenantAdminSummaryResponse {
  tenantId: string;
  name: string;
  status: TenantStatus;
  licenseType: AdminLicenseType;
  monthlyDocumentCount: number;
  monthlyDocumentLimit: number | null;
  licenseExpiresAt: string;
}

export interface TenantAdminDetailResponse {
  summary: TenantAdminSummaryResponse;
  ocrReadLimitOverride: number | null;
  ocrLayoutLimitOverride: number | null;
  ocrDocumentLimitOverride: number | null;
  discoveryOcrProviderIdOverride: string | null;
  processingAutoRoutingOverride: boolean | null;
  complexDocOcrProviderIdOverride: string | null;
  canEditOcrSource: boolean;
  /** Force auto-classification on/off for this tenant; null = inherit the plan flag. */
  autoClassificationOverride: boolean | null;
}

export interface TenantPermissionsRequest {
  /** When true, the tenant's users may choose the OCR source of its document types. */
  canEditOcrSource: boolean;
}

export interface TenantOcrLimitsRequest {
  readCallsPerMonth: number | null;
  layoutCallsPerMonth: number | null;
  documentCallsPerMonth: number | null;
}

export interface TenantOcrRoutingRequest {
  /** Provider id for discovery, the `__di__` sentinel to pin Document Intelligence, or null to inherit. */
  discoveryOcrProviderIdOverride: string | null;
  /** Force processing auto-routing on/off for this tenant; null = inherit the plan. */
  processingAutoRoutingOverride: boolean | null;
  /** Provider id complex/tabular documents route to; null = inherit the plan. */
  complexDocOcrProviderIdOverride: string | null;
}

/** Request body for `PATCH /admin/tenants/{id}/classification`. */
export interface TenantClassificationRequest {
  /** Force auto-classification on/off for this tenant; null = inherit the plan flag. */
  autoClassificationOverride: boolean | null;
}

/** Sentinel for {@link TenantOcrRoutingRequest.discoveryOcrProviderIdOverride} meaning "pin Document Intelligence". */
export const FORCE_DI_SENTINEL = '__di__';

export interface PagedAdminTenantsResponse {
  items: TenantAdminSummaryResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface AdminTenantsFilter {
  search?: string;
  status?: TenantStatus | '';
  licenseType?: AdminLicenseType | '';
  page?: number;
  pageSize?: number;
}

export interface CreateTenantAdminRequest {
  tenantId: string;
  name: string;
  licenseType: AdminLicenseType;
  licenseExpiresAt: string;
  apiKeyPlaintext: string;
}

export interface UpdateTenantStatusRequest {
  newStatus: TenantStatus;
}

export interface ChangeTenantLicenseRequest {
  newLicense: AdminLicenseType;
  expiresAt: string;
}

// ---------- Metrics DTOs -----------------------------------------------------

export interface TenantUsageSummary {
  tenantId: string;
  name: string;
  monthlyCount: number;
}

export interface PlanDistribution {
  plan: AdminLicenseType;
  count: number;
}

export interface PlatformMetricsResponse {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  blockedTenants: number;
  documentsProcessedThisMonth: number;
  topTenantsByUsage: TenantUsageSummary[];
  tenantsByPlan: PlanDistribution[];
}

// ---------- License plan DTOs ------------------------------------------------

export interface PlatformProviderAccessResponse {
  platformProviderId: string;
  // displayName is not serialized by the backend's plan DTO; rendered as fallback to id in the UI.
  displayName?: string;
  monthlyCallLimit: number | null;
}

export interface LicensePlanDefinitionResponse {
  licenseType: AdminLicenseType;
  displayName: string;
  hasCost: boolean;
  monthlyCostAmount: number | null;
  costCurrency: string;
  documentsPerMonth: number | null;
  maxDocumentTypes: number | null;
  maxUsers: number | null;
  jobRetentionDays: number;
  diRequestsPerMonth: number | null;
  diPagesPerMonth: number | null;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  allowedPlatformProviders: PlatformProviderAccessResponse[];
  isBuiltIn: boolean;
  readCallsPerMonth: number | null;
  layoutCallsPerMonth: number | null;
  documentCallsPerMonth: number | null;
  autoOcrRoutingEnabled: boolean;
  complexDocOcrProviderId: string | null;
  /** Opt-in: auto-classify untyped uploads against the tenant's published types before extraction. */
  autoClassificationEnabled: boolean;
}

export interface CreateLicensePlanRequest {
  licenseType: string;
  displayName: string;
  hasCost: boolean;
  monthlyCostAmount: number | null;
  costCurrency: string;
  documentsPerMonth: number | null;
  maxDocumentTypes: number | null;
  maxUsers: number | null;
  jobRetentionDays: number;
  diRequestsPerMonth: number | null;
  diPagesPerMonth: number | null;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  allowedPlatformProviders: PlatformProviderAccessRequest[] | null;
  readCallsPerMonth: number | null;
  layoutCallsPerMonth: number | null;
  documentCallsPerMonth: number | null;
  autoOcrRoutingEnabled: boolean;
  complexDocOcrProviderId: string | null;
  /** Opt-in: auto-classify untyped uploads against the tenant's published types before extraction. */
  autoClassificationEnabled: boolean;
}

export interface PlatformProviderAccessRequest {
  platformProviderId: string;
  monthlyCallLimit: number | null;
}

export interface UpdateLicensePlanRequest {
  displayName: string;
  hasCost: boolean;
  monthlyCostAmount: number | null;
  costCurrency: string;
  documentsPerMonth: number | null;
  maxDocumentTypes: number | null;
  maxUsers: number | null;
  jobRetentionDays: number;
  diRequestsPerMonth: number | null;
  diPagesPerMonth: number | null;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  allowedPlatformProviders: PlatformProviderAccessRequest[];
  readCallsPerMonth: number | null;
  layoutCallsPerMonth: number | null;
  documentCallsPerMonth: number | null;
  autoOcrRoutingEnabled: boolean;
  complexDocOcrProviderId: string | null;
  /** Opt-in: auto-classify untyped uploads against the tenant's published types before extraction. */
  autoClassificationEnabled: boolean;
}

// ---------- Platform provider DTOs ------------------------------------------

/**
 * Inbound custom header on a provider request. Mirrors `ProviderHeaderInput` on the backend.
 * For `isSecret = true`, `value` is the plaintext to be stored in Key Vault and is never
 * persisted to Cosmos in the clear.
 */
export interface ProviderHeaderInput {
  name: string;
  value: string;
  isSecret: boolean;
}

/**
 * Outbound projection. For secrets, `value` is the Key Vault secret name (never the credential).
 */
export interface ProviderHeaderView {
  name: string;
  value: string;
  isSecret: boolean;
}

export interface PlatformProviderResponse {
  id: string;
  providerType: ProviderType;
  displayName: string;
  modelName: string;
  endpoint: string | null;
  isActive: boolean;
  keyVaultSecretRef: string;
  customHeaders: ProviderHeaderView[];
  /** True when this provider is the auto-classification default LLM (at most one across the platform). */
  isClassificationDefault: boolean;
}

export interface CreatePlatformProviderRequest {
  providerType: ProviderType;
  displayName: string;
  modelName: string;
  endpoint: string | null;
  apiKeyPlaintext: string;
  customHeaders?: ProviderHeaderInput[];
  /** When true, this provider becomes the auto-classification default LLM (clears any other). */
  isClassificationDefault?: boolean;
}

export interface UpdatePlatformProviderRequest {
  displayName?: string;
  modelName?: string;
  endpoint?: string | null;
  /** Non-null rotates the API key in Key Vault. */
  apiKeyPlaintext?: string;
  /** Non-null replaces the entire header list (empty array clears it). */
  customHeaders?: ProviderHeaderInput[];
  /** Non-null sets/clears the classification-default flag; true clears it from every other provider. */
  isClassificationDefault?: boolean;
}

export interface ProviderTestResponse {
  success: boolean;
  latencyMs: number | null;
  errorMessage: string | null;
}

// ---------- DI resource DTOs -------------------------------------------------

export interface DiResourceResponse {
  id: string;
  displayName: string;
  endpoint: string;
  assignedLicenses: AdminLicenseType[];
  isActive: boolean;
}

export interface CreateDiResourceRequest {
  displayName: string;
  endpoint: string;
  apiKeyPlaintext: string;
  assignedLicenses: AdminLicenseType[];
}

export interface UpdateDiResourceRequest {
  displayName?: string;
  endpoint?: string;
  assignedLicenses?: AdminLicenseType[];
}

// ---------- OCR provider DTOs ------------------------------------------------
// Multi-provider OCR: Azure Document Intelligence (per-license default) + Mistral OCR/Document AI on
// Azure AI Foundry. Mirrors src/DocuSense.Application/Dtos/Admin/OcrProviderDtos.cs +
// OcrProviderModelCatalog.cs. Secrets are never returned — only the Key Vault reference.

/** OcrProviderKind enum name (PascalCase) — mirrors DocuSense.Domain.Enums.OcrProviderKind. */
export const OcrProviderKinds = {
  AzureDocumentIntelligence: 'AzureDocumentIntelligence',
  Mistral: 'Mistral',
} as const;
export type OcrProviderKind = (typeof OcrProviderKinds)[keyof typeof OcrProviderKinds];

/** OcrAuthMode enum name (PascalCase) — mirrors DocuSense.Domain.Enums.OcrAuthMode. */
export const OcrAuthModes = {
  ApiKey: 'ApiKey',
  ManagedIdentity: 'ManagedIdentity',
} as const;
export type OcrAuthMode = (typeof OcrAuthModes)[keyof typeof OcrAuthModes];

/** OcrMode enum name (PascalCase) — mirrors DocuSense.Domain.Enums.OcrMode. */
export const OcrModes = {
  Ocr: 'Ocr',
  DocumentAi: 'DocumentAi',
  Batch: 'Batch',
} as const;
export type OcrMode = (typeof OcrModes)[keyof typeof OcrModes];

/** Selectable OCR modes with their human labels for the admin form. */
export const OCR_MODE_OPTIONS: ReadonlyArray<{ value: OcrMode; label: string }> = [
  { value: 'Ocr', label: 'OCR → LLM (por defecto)' },
  { value: 'DocumentAi', label: 'Document AI directo' },
  { value: 'Batch', label: 'Batch (asíncrono)' },
];

/** MistralHosting enum name (PascalCase) — mirrors DocuSense.Domain.Enums.MistralHosting. */
export const MistralHostings = {
  AzureFoundry: 'AzureFoundry',
  MistralApi: 'MistralApi',
} as const;
export type MistralHosting = (typeof MistralHostings)[keyof typeof MistralHostings];

/** Where a Mistral provider is reached. Direct API bypasses Azure Foundry entirely. */
export const MISTRAL_HOSTING_OPTIONS: ReadonlyArray<{ value: MistralHosting; label: string }> = [
  { value: 'AzureFoundry', label: 'Azure AI Foundry' },
  { value: 'MistralApi', label: 'API directa de Mistral' },
];

/** Full OCR URL prefilled when the admin picks the Mistral direct API. Used VERBATIM (no path appended). */
export const MISTRAL_DIRECT_API_OCR_URL = 'https://api.mistral.ai/v1/ocr';

/** OCR capability flag names — must match DocuSense.Domain.Enums.OcrCapabilities member names. */
export const OcrCapabilities = {
  Text: 'Text',
  Tables: 'Tables',
  Structure: 'Structure',
  KeyValuePairs: 'KeyValuePairs',
} as const;
export type OcrCapability = (typeof OcrCapabilities)[keyof typeof OcrCapabilities];

export const OCR_CAPABILITY_OPTIONS: ReadonlyArray<{ value: OcrCapability; label: string }> = [
  { value: 'Text', label: 'Texto' },
  { value: 'Tables', label: 'Tablas' },
  { value: 'Structure', label: 'Estructura' },
  { value: 'KeyValuePairs', label: 'Pares clave/valor' },
];

export interface OcrProviderResponse {
  id: string;
  displayName: string;
  kind: OcrProviderKind;
  modelId: string | null;
  endpoint: string;
  region: string | null;
  subscriptionId: string | null;
  authMode: OcrAuthMode;
  keyVaultSecretRef: string | null;
  capabilities: string[];
  usageLimitMonthlyCalls: number | null;
  usageLimitMonthlyPages: number | null;
  assignedLicenses: AdminLicenseType[];
  isActive: boolean;
  /** True when this provider is the discovery pipeline's default OCR backend (at most one). */
  isDiscoveryDefault: boolean;
  /** How the provider operates: OCR → LLM / Document AI direct / Batch. */
  mode: OcrMode;
  /** For Mistral: Azure Foundry or Mistral's direct API. */
  hosting: MistralHosting;
}

export interface CreateOcrProviderRequest {
  kind: OcrProviderKind;
  displayName: string;
  modelId?: string | null;
  endpoint: string;
  region?: string | null;
  subscriptionId?: string | null;
  authMode: OcrAuthMode;
  /** Plaintext API key; stored in Key Vault. Required for ApiKey auth. */
  apiKeyPlaintext?: string | null;
  capabilities: string[];
  usageLimitMonthlyCalls?: number | null;
  usageLimitMonthlyPages?: number | null;
  assignedLicenses: AdminLicenseType[];
  /** When true, becomes the discovery pipeline's default OCR backend (clears any other). */
  isDiscoveryDefault?: boolean;
  /** How the provider operates; defaults to Ocr when omitted. */
  mode?: OcrMode;
  /** Where a Mistral provider is reached; defaults to AzureFoundry. MistralApi requires ApiKey auth. */
  hosting?: MistralHosting;
}

export interface UpdateOcrProviderRequest {
  displayName?: string;
  modelId?: string | null;
  endpoint?: string;
  region?: string | null;
  subscriptionId?: string | null;
  authMode?: OcrAuthMode;
  /** Non-null rotates the API key in Key Vault; omit/blank to keep the current key. */
  apiKeyPlaintext?: string | null;
  capabilities?: string[];
  usageLimitMonthlyCalls?: number | null;
  usageLimitMonthlyPages?: number | null;
  assignedLicenses?: AdminLicenseType[];
  /** When non-null, sets/clears the discovery-default flag; true clears it from every other provider. */
  isDiscoveryDefault?: boolean;
  /** When set, updates how the provider operates (OCR → LLM / Document AI / Batch). */
  mode?: OcrMode;
  /** When set, updates where a Mistral provider is reached (Azure Foundry / Mistral direct API). */
  hosting?: MistralHosting;
}

/** One selectable OCR model for a provider kind. Mirrors OcrProviderModelEntry. */
export interface OcrProviderModelEntry {
  modelId: string;
  displayName: string;
}

/** Mirrors OcrProviderModelsResponse — suggested models for a kind (admin can still type a new one). */
export interface OcrProviderModelsResponse {
  kind: OcrProviderKind;
  models: OcrProviderModelEntry[];
}

// ---------- Fetchers ---------------------------------------------------------

const BASE = '/api/v1/admin';

function buildTenantsQs(filter: AdminTenantsFilter): string {
  const p = new URLSearchParams();
  if (filter.search) p.set('search', filter.search);
  if (filter.status) p.set('status', filter.status);
  if (filter.licenseType) p.set('licenseType', filter.licenseType);
  if (filter.page !== undefined) p.set('page', String(filter.page));
  if (filter.pageSize !== undefined) p.set('pageSize', String(filter.pageSize));
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export function listAdminTenants(
  filter: AdminTenantsFilter,
  signal?: AbortSignal,
): Promise<PagedAdminTenantsResponse> {
  return adminFetch<PagedAdminTenantsResponse>(
    `${BASE}/tenants${buildTenantsQs(filter)}`,
    signal !== undefined ? { signal } : {},
  );
}

export function getAdminTenantDetail(id: string, signal?: AbortSignal): Promise<TenantAdminDetailResponse> {
  return adminFetch<TenantAdminDetailResponse>(`${BASE}/tenants/${encodeURIComponent(id)}`, signal !== undefined ? { signal } : {});
}

export function createAdminTenant(request: CreateTenantAdminRequest): Promise<TenantAdminSummaryResponse> {
  return adminFetch<TenantAdminSummaryResponse>(`${BASE}/tenants`, { method: 'POST', json: request });
}

// ---------- Admin per-tenant ROI + cost (Phase C4 — GET /admin/tenants/{id}/roi) ----------
//
// Admin-only: unlike the tenant-facing ROI view, this carries DocuSense's COGS, margin and per-model
// breakdown (model names included). Never rendered outside the platform-admin panel.

/** Mirrors `AdminModelCostRow` on the API. */
export interface AdminModelCostRow {
  modelName: string;
  callCount: number;
  cost: number;
  currency: string;
  averageLatencyMs: number;
  successRate: number;
}

/** Mirrors `AdminTenantRoiResponse` on the API. */
export interface AdminTenantRoiResponse {
  tenantId: string;
  periodDays: number;
  currency: string;
  hoursSaved: number;
  savingsAmount: number;
  automationRate: number;
  documentsProcessed: number;
  aiCost: number;
  aiCostCurrency: string;
  subscriptionMonthlyCost: number | null;
  margin: number | null;
  valueToPriceRatio: number | null;
  churnRisk: boolean;
  modelBreakdown: AdminModelCostRow[];
}

/** Admin-only per-tenant ROI + cost (value, COGS, margin, churn ratio, per-model breakdown). */
export function getAdminTenantRoi(id: string, signal?: AbortSignal): Promise<AdminTenantRoiResponse> {
  return adminFetch<AdminTenantRoiResponse>(
    `${BASE}/tenants/${encodeURIComponent(id)}/roi`,
    signal !== undefined ? { signal } : {},
  );
}

export function updateTenantStatus(id: string, request: UpdateTenantStatusRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/status`, { method: 'PATCH', json: request });
}

export function changeTenantLicense(id: string, request: ChangeTenantLicenseRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/license`, { method: 'PATCH', json: request });
}

export function setTenantOcrLimits(id: string, request: TenantOcrLimitsRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/ocr-limits`, { method: 'PATCH', json: request });
}

export function setTenantOcrRouting(id: string, request: TenantOcrRoutingRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/ocr-routing`, { method: 'PATCH', json: request });
}

export function setTenantClassification(id: string, request: TenantClassificationRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/classification`, { method: 'PATCH', json: request });
}

export function setTenantPermissions(id: string, request: TenantPermissionsRequest): Promise<null> {
  return adminFetch<null>(`${BASE}/tenants/${encodeURIComponent(id)}/permissions`, { method: 'PATCH', json: request });
}

export function getPlatformMetrics(signal?: AbortSignal): Promise<PlatformMetricsResponse> {
  return adminFetch<PlatformMetricsResponse>(`${BASE}/metrics`, signal !== undefined ? { signal } : {});
}

export function listLicensePlans(signal?: AbortSignal): Promise<LicensePlanDefinitionResponse[]> {
  return adminFetch<LicensePlanDefinitionResponse[]>(`${BASE}/plans`, signal !== undefined ? { signal } : {});
}

export function updateLicensePlan(licenseType: string, request: UpdateLicensePlanRequest): Promise<LicensePlanDefinitionResponse> {
  return adminFetch<LicensePlanDefinitionResponse>(
    `${BASE}/plans/${encodeURIComponent(licenseType)}`,
    { method: 'PATCH', json: request },
  );
}

export function createLicensePlan(request: CreateLicensePlanRequest): Promise<LicensePlanDefinitionResponse> {
  return adminFetch<LicensePlanDefinitionResponse>(`${BASE}/plans`, { method: 'POST', json: request });
}

export function deleteLicensePlan(licenseType: string): Promise<null> {
  return adminFetch<null>(`${BASE}/plans/${encodeURIComponent(licenseType)}`, { method: 'DELETE' });
}

export function listPlatformProviders(signal?: AbortSignal): Promise<PlatformProviderResponse[]> {
  return adminFetch<PlatformProviderResponse[]>(`${BASE}/platform-providers`, signal !== undefined ? { signal } : {});
}

export function createPlatformProvider(request: CreatePlatformProviderRequest): Promise<PlatformProviderResponse> {
  return adminFetch<PlatformProviderResponse>(`${BASE}/platform-providers`, { method: 'POST', json: request });
}

export function updatePlatformProvider(id: string, request: UpdatePlatformProviderRequest): Promise<PlatformProviderResponse> {
  return adminFetch<PlatformProviderResponse>(
    `${BASE}/platform-providers/${encodeURIComponent(id)}`,
    { method: 'PUT', json: request },
  );
}

export function deletePlatformProvider(id: string): Promise<null> {
  return adminFetch<null>(`${BASE}/platform-providers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function testPlatformProvider(id: string): Promise<ProviderTestResponse> {
  return adminFetch<ProviderTestResponse>(
    `${BASE}/platform-providers/${encodeURIComponent(id)}/test`,
    { method: 'POST' },
  );
}

export function listDiResources(signal?: AbortSignal): Promise<DiResourceResponse[]> {
  return adminFetch<DiResourceResponse[]>(`${BASE}/di-resources`, signal !== undefined ? { signal } : {});
}

export function createDiResource(request: CreateDiResourceRequest): Promise<DiResourceResponse> {
  return adminFetch<DiResourceResponse>(`${BASE}/di-resources`, { method: 'POST', json: request });
}

export function updateDiResource(id: string, request: UpdateDiResourceRequest): Promise<DiResourceResponse> {
  return adminFetch<DiResourceResponse>(
    `${BASE}/di-resources/${encodeURIComponent(id)}`,
    { method: 'PUT', json: request },
  );
}

export function deleteDiResource(id: string): Promise<null> {
  return adminFetch<null>(`${BASE}/di-resources/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function testDiResource(id: string): Promise<ProviderTestResponse> {
  return adminFetch<ProviderTestResponse>(
    `${BASE}/di-resources/${encodeURIComponent(id)}/test`,
    { method: 'POST' },
  );
}

// ---------- OCR provider fetchers --------------------------------------------

export function listOcrProviders(signal?: AbortSignal): Promise<OcrProviderResponse[]> {
  return adminFetch<OcrProviderResponse[]>(`${BASE}/ocr-providers`, signal !== undefined ? { signal } : {});
}

export function createOcrProvider(request: CreateOcrProviderRequest): Promise<OcrProviderResponse> {
  return adminFetch<OcrProviderResponse>(`${BASE}/ocr-providers`, { method: 'POST', json: request });
}

export function updateOcrProvider(id: string, request: UpdateOcrProviderRequest): Promise<OcrProviderResponse> {
  return adminFetch<OcrProviderResponse>(
    `${BASE}/ocr-providers/${encodeURIComponent(id)}`,
    { method: 'PUT', json: request },
  );
}

export function deleteOcrProvider(id: string): Promise<null> {
  return adminFetch<null>(`${BASE}/ocr-providers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function testOcrProvider(id: string): Promise<ProviderTestResponse> {
  return adminFetch<ProviderTestResponse>(
    `${BASE}/ocr-providers/${encodeURIComponent(id)}/test`,
    { method: 'POST' },
  );
}

export function getOcrProviderModels(
  kind: OcrProviderKind,
  signal?: AbortSignal,
): Promise<OcrProviderModelsResponse> {
  return adminFetch<OcrProviderModelsResponse>(
    `${BASE}/ocr-providers/models?kind=${encodeURIComponent(kind)}`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Advanced metrics DTOs (operational / billing / tech-health) ------

export interface DailyDocumentCountResponse {
  day: string; // yyyy-MM-dd
  total: number;
  succeeded: number;
  failed: number;
}

export interface RecentJobFailureResponse {
  tenantId: string;
  jobId: string;
  failureReason: string | null;
  submittedAt: string;
}

export interface TenantNearQuotaResponse {
  tenantId: string;
  name: string;
  licenseType: AdminLicenseType;
  monthlyDocumentCount: number;
  monthlyDocumentLimit: number;
  usagePercent: number;
}

export interface OperationalMetricsResponse {
  docsLast24h: number;
  docsLast7d: number;
  docsLast30d: number;
  successRate7d: number;
  dailyLast7d: DailyDocumentCountResponse[];
  recentFailures: RecentJobFailureResponse[];
  tenantsNearQuota: TenantNearQuotaResponse[];
}

export interface PlanRevenueResponse {
  licenseType: AdminLicenseType;
  tenantCount: number;
  monthlyCostAmount: number | null;
  costCurrency: string;
  projectedMonthlyRevenue: number;
}

export interface BillingMetricsResponse {
  estimatedMrr: number;
  currency: string;
  payingTenants: number;
  freeTenants: number;
  byPlan: PlanRevenueResponse[];
}

export type DocumentJobStatus =
  | 'Queued'
  | 'Processing'
  | 'Approved'
  | 'ReviewRequired'
  | 'Rejected'
  | 'Failed';

export interface JobStatusCountResponse {
  status: DocumentJobStatus;
  count: number;
}

export interface TechHealthMetricsResponse {
  queueDepth: number;
  averageProcessingMs: number | null;
  statusBreakdown7d: JobStatusCountResponse[];
}

export function getOperationalMetrics(signal?: AbortSignal): Promise<OperationalMetricsResponse> {
  return adminFetch<OperationalMetricsResponse>(
    `${BASE}/metrics/operational`,
    signal !== undefined ? { signal } : {},
  );
}

export function getBillingMetrics(signal?: AbortSignal): Promise<BillingMetricsResponse> {
  return adminFetch<BillingMetricsResponse>(
    `${BASE}/metrics/billing`,
    signal !== undefined ? { signal } : {},
  );
}

export function getTechHealthMetrics(signal?: AbortSignal): Promise<TechHealthMetricsResponse> {
  return adminFetch<TechHealthMetricsResponse>(
    `${BASE}/metrics/tech-health`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Prompt template DTOs --------------------------------------------

export const PromptKinds = {
  Extraction: 'Extraction',
  Discovery: 'Discovery',
  ProviderTest: 'ProviderTest',
  Segmentation: 'Segmentation',
  Reeducation: 'Reeducation',
  Classification: 'Classification',
} as const;
export type PromptKind = (typeof PromptKinds)[keyof typeof PromptKinds];

export interface PromptTemplateSummaryResponse {
  kind: PromptKind;
  activeVersionId: string;
  activeVersionNumber: number;
  totalVersions: number;
  updatedAt: string;
  updatedBy: string;
}

export interface PromptTokenResponse {
  name: string;
  description: string;
}

export interface PromptTemplateVersionSummaryResponse {
  versionId: string;
  number: number;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
}

export interface PromptTemplateDetailResponse {
  kind: PromptKind;
  activeVersionId: string;
  activeSystemTemplate: string;
  activeUserTemplate: string | null;
  versions: PromptTemplateVersionSummaryResponse[];
  tokens: PromptTokenResponse[];
  updatedAt: string;
  updatedBy: string;
}

export interface PromptTemplateVersionResponse {
  kind: PromptKind;
  versionId: string;
  number: number;
  systemTemplate: string;
  userTemplate: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
}

export interface CreatePromptVersionRequest {
  systemTemplate: string;
  userTemplate?: string | null;
  notes?: string | null;
}

export interface PromptPreviewRequest {
  systemTemplate: string;
  userTemplate?: string | null;
}

export interface PromptPreviewResultResponse {
  kind: PromptKind;
  renderedSystem: string;
  renderedUser: string | null;
}

export interface PromptDefaultTemplateResponse {
  kind: PromptKind;
  systemTemplate: string;
  userTemplate: string | null;
}

export function listPromptTemplates(signal?: AbortSignal): Promise<PromptTemplateSummaryResponse[]> {
  return adminFetch<PromptTemplateSummaryResponse[]>(
    `${BASE}/prompts`,
    signal !== undefined ? { signal } : {},
  );
}

export function getPromptTemplate(kind: PromptKind, signal?: AbortSignal): Promise<PromptTemplateDetailResponse> {
  return adminFetch<PromptTemplateDetailResponse>(
    `${BASE}/prompts/${kind}`,
    signal !== undefined ? { signal } : {},
  );
}

export function getPromptTemplateVersion(
  kind: PromptKind,
  versionId: string,
  signal?: AbortSignal,
): Promise<PromptTemplateVersionResponse> {
  return adminFetch<PromptTemplateVersionResponse>(
    `${BASE}/prompts/${kind}/versions/${versionId}`,
    signal !== undefined ? { signal } : {},
  );
}

export function createPromptVersion(
  kind: PromptKind,
  body: CreatePromptVersionRequest,
): Promise<PromptTemplateVersionResponse> {
  return adminFetch<PromptTemplateVersionResponse>(
    `${BASE}/prompts/${kind}/versions`,
    { method: 'POST', json: body },
  );
}

export function activatePromptVersion(kind: PromptKind, versionId: string): Promise<null> {
  return adminFetch<null>(
    `${BASE}/prompts/${kind}/activate/${versionId}`,
    { method: 'POST' },
  );
}

export function previewPrompt(
  kind: PromptKind,
  body: PromptPreviewRequest,
): Promise<PromptPreviewResultResponse> {
  return adminFetch<PromptPreviewResultResponse>(
    `${BASE}/prompts/${kind}/preview`,
    { method: 'POST', json: body },
  );
}

export function getPromptDefault(
  kind: PromptKind,
  signal?: AbortSignal,
): Promise<PromptDefaultTemplateResponse> {
  return adminFetch<PromptDefaultTemplateResponse>(
    `${BASE}/prompts/${kind}/default`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- AI usage dashboard (Fase 3 backend) ------------------------------

export type AiUsageGroupBy = 'Day' | 'Model' | 'ProviderType' | 'Tenant' | 'PromptKind';
export type AiUsageScope = 'Platform' | 'Tenant';
export type AiUsagePromptKind = 'Extraction' | 'Discovery' | 'ProviderTest' | 'Segmentation' | 'Reeducation' | 'Classification';

export interface AiUsageMetricsFilter {
  from?: string; // ISO-8601
  to?: string;
  tenantId?: string;
  providerScope?: AiUsageScope;
  providerType?: ProviderType;
  modelName?: string;
  promptKind?: AiUsagePromptKind;
  promptTemplateVersionId?: string;
  groupBy?: AiUsageGroupBy;
  topCount?: number;
}

export interface AiUsageSummary {
  callCount: number;
  inputTokensTotal: number;
  outputTokensTotal: number;
  costTotal: number;
  currency: string;
  successRate: number;
  averageLatencyMs: number;
}

export interface AiUsageTimeSeriesPoint {
  bucketKey: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  callCount: number;
}

export interface AiUsageBreakdownRow {
  key: string;
  label: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  callCount: number;
  averageLatencyMs: number;
  p95LatencyMs: number;
}

export interface AiUsageMetricsResponse {
  summary: AiUsageSummary;
  series: AiUsageTimeSeriesPoint[];
  top: AiUsageBreakdownRow[];
  latency: AiUsageBreakdownRow[];
}

export interface ModelPriceResponse {
  id: string;
  providerType: ProviderType;
  modelName: string;
  inputPricePer1KTokens: number;
  outputPricePer1KTokens: number;
  currency: string;
  source: 'Seed' | 'OpenRouter' | 'OpenAiUsage' | 'Manual';
  effectiveFrom: string;
  updatedBy: string;
  updatedAt: string;
}

export interface UpsertManualModelPriceRequest {
  providerType: ProviderType;
  modelName: string;
  inputPricePer1KTokens: number;
  outputPricePer1KTokens: number;
  currency: string;
}

export interface ModelPriceHistoryEntryResponse {
  id: string;
  providerType: ProviderType;
  modelName: string;
  inputPricePer1KTokens: number;
  outputPricePer1KTokens: number;
  currency: string;
  source: 'Seed' | 'OpenRouter' | 'OpenAiUsage' | 'Manual';
  capturedBy: string;
  capturedAt: string;
}

export interface ModelPricesRefreshResponse {
  openRouter: { fetched: number; upserted: number; skipped: number } | null;
  openAiUsage:
    | {
        skipped: boolean;
        skipReason: string | null;
        modelsObserved: number;
        totalInputTokens: number;
        totalOutputTokens: number;
        totalRequests: number;
      }
    | null;
}

export interface ProviderModelEntryResponse {
  modelId: string;
  displayName: string | null;
}

export interface ProviderModelsResponse {
  providerId: string;
  fetchedAt: string;
  notApplicable: boolean;
  fetchError: string | null;
  models: ProviderModelEntryResponse[];
}

function buildAiUsageQs(filter: AiUsageMetricsFilter): string {
  const p = new URLSearchParams();
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  if (filter.tenantId) p.set('tenantId', filter.tenantId);
  if (filter.providerScope) p.set('providerScope', filter.providerScope);
  if (filter.providerType) p.set('providerType', filter.providerType);
  if (filter.modelName) p.set('modelName', filter.modelName);
  if (filter.promptKind) p.set('promptKind', filter.promptKind);
  if (filter.promptTemplateVersionId) p.set('promptTemplateVersionId', filter.promptTemplateVersionId);
  if (filter.groupBy) p.set('groupBy', filter.groupBy);
  if (filter.topCount !== undefined) p.set('topCount', String(filter.topCount));
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export function getAiUsageMetrics(
  filter: AiUsageMetricsFilter,
  signal?: AbortSignal,
): Promise<AiUsageMetricsResponse> {
  return adminFetch<AiUsageMetricsResponse>(
    `${BASE}/metrics/ai-usage${buildAiUsageQs(filter)}`,
    signal !== undefined ? { signal } : {},
  );
}

export function listModelPrices(signal?: AbortSignal): Promise<ModelPriceResponse[]> {
  return adminFetch<ModelPriceResponse[]>(
    `${BASE}/model-prices`,
    signal !== undefined ? { signal } : {},
  );
}

export function upsertManualModelPrice(
  request: UpsertManualModelPriceRequest,
): Promise<ModelPriceResponse> {
  return adminFetch<ModelPriceResponse>(`${BASE}/model-prices`, { method: 'POST', json: request });
}

export function deleteManualModelPrice(providerType: ProviderType, modelName: string): Promise<null> {
  return adminFetch<null>(
    `${BASE}/model-prices/${encodeURIComponent(providerType)}/${encodeURIComponent(modelName)}`,
    { method: 'DELETE' },
  );
}

export function refreshModelPrices(): Promise<ModelPricesRefreshResponse> {
  return adminFetch<ModelPricesRefreshResponse>(`${BASE}/model-prices/refresh`, { method: 'POST' });
}

/** Mirrors backend `UnresolvedModelRow`. */
export interface UnresolvedModelRow {
  providerType: ProviderType;
  modelName: string;
  /** Last candidate the normaliser would have tried — usually the alias the admin should price. */
  suggestedAlias: string;
  callCount: number;
  firstSeen: string;
  lastSeen: string;
}

/** Mirrors backend `UnresolvedModelsResponse`. */
export interface UnresolvedModelsResponse {
  items: UnresolvedModelRow[];
}

/**
 * Lists every (providerType, modelName) pair that produced AI calls in the last `windowDays` (default 30)
 * and whose price the resolver still cannot find — neither by exact match nor through the alias
 * fallback. Drives the "modelos sin precio" banner on the pricing page.
 */
export function listUnresolvedModels(
  windowDays?: number,
  signal?: AbortSignal,
): Promise<UnresolvedModelsResponse> {
  const qs = typeof windowDays === 'number' ? `?windowDays=${windowDays}` : '';
  return adminFetch<UnresolvedModelsResponse>(
    `${BASE}/ai-usage/unresolved-models${qs}`,
    signal !== undefined ? { signal } : {},
  );
}

export function getModelPriceHistory(
  providerType: ProviderType,
  modelName: string,
  signal?: AbortSignal,
): Promise<ModelPriceHistoryEntryResponse[]> {
  return adminFetch<ModelPriceHistoryEntryResponse[]>(
    `${BASE}/model-prices/${encodeURIComponent(providerType)}/${encodeURIComponent(modelName)}/history`,
    signal !== undefined ? { signal } : {},
  );
}

/**
 * Downloads the raw AI-usage rows as CSV. Bypasses <c>adminFetch</c> because the response body is
 * not JSON; otherwise honours the same auth + base-URL plumbing.
 */
export async function downloadAiUsageCsv(filter: AiUsageMetricsFilter): Promise<Blob> {
  const token = AdminTokenStore.get();
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (token !== null) headers['Authorization'] = `Bearer ${token.accessToken}`;

  const raw = (import.meta.env as Record<string, string | undefined>).VITE_API_BASE_URL?.trim() ?? '';
  const base = raw.endsWith('/') ? raw.slice(0, -1) : raw;
  const url = `${base}${BASE}/metrics/ai-usage/export${buildAiUsageQs(filter)}`;

  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.blob();
}

export function getProviderModels(providerId: string, signal?: AbortSignal): Promise<ProviderModelsResponse> {
  return adminFetch<ProviderModelsResponse>(
    `${BASE}/platform-providers/${encodeURIComponent(providerId)}/models`,
    signal !== undefined ? { signal } : {},
  );
}

export function refreshProviderModels(providerId: string): Promise<ProviderModelsResponse> {
  return adminFetch<ProviderModelsResponse>(
    `${BASE}/platform-providers/${encodeURIComponent(providerId)}/models/refresh`,
    { method: 'POST' },
  );
}

// ---------- Preparation (Fase 8) — effective policy viewer + CU governance editor ----------

/** Response shape for GET /api/v1/admin/preparation/policies/{tenantId}/effective. */
export interface EffectivePolicyResponse {
  chain: string[];
  source: 'tenant' | 'house';
  forbiddenStripped: string[];
}

/** Per-bucket spend cap (USD + soft/hard percentages of it). */
export interface CostCapRequest {
  usd: number;
  softPct: number;
  hardPct: number;
}

/** CU governance gates (plan §5b five-control gate). */
export interface CuGovernanceRequest {
  tenantEnabled: boolean;
  allowedDocumentTypeIds?: string[];
  cuFileTypes?: string[];
  dailyCap?: CostCapRequest;
  monthlyCap?: CostCapRequest;
}

/** Request body for PUT /api/v1/admin/preparation/policies/{tenantId}. */
export interface UpsertRoutingPolicyRequest {
  documentTypeId?: string;
  preferredChain?: string[];
  forbiddenRoutes?: string[];
  cuGovernance?: CuGovernanceRequest;
}

export interface GetEffectivePolicyParams {
  tenantId: string;
  mime: string;
  digital: boolean;
  documentTypeId?: string;
}

export function getEffectivePolicy(
  params: GetEffectivePolicyParams,
  signal?: AbortSignal,
): Promise<EffectivePolicyResponse> {
  const qs = new URLSearchParams({ mime: params.mime, digital: String(params.digital) });
  if (params.documentTypeId) qs.set('documentTypeId', params.documentTypeId);
  const path = `${BASE}/preparation/policies/${encodeURIComponent(params.tenantId)}/effective?${qs.toString()}`;
  return adminFetch<EffectivePolicyResponse>(path, signal !== undefined ? { signal } : {});
}

export function upsertRoutingPolicy(
  tenantId: string,
  request: UpsertRoutingPolicyRequest,
): Promise<void> {
  return adminFetch<void>(`${BASE}/preparation/policies/${encodeURIComponent(tenantId)}`, {
    method: 'PUT',
    json: request,
  });
}

// ---------- Email alerts (Phase B) ----------

export interface AdminEmailSenderResponse {
  id: string;
  fromAddress: string;
  displayName: string;
  replyTo: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertEmailSenderBody {
  id?: string | null;
  fromAddress: string;
  displayName: string;
  replyTo?: string | null;
  isActive: boolean;
  isDefault: boolean;
}

export interface AdminAlertTemplateVersionSummary {
  versionId: string;
  number: number;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
}

export interface AdminAlertTemplateDetail {
  alertType: string;
  activeVersionId: string;
  activeSubject: string;
  activeHtmlBody: string;
  versions: AdminAlertTemplateVersionSummary[];
  tokens: { name: string }[];
  updatedAt: string;
  updatedBy: string;
}

export interface AdminAlertTemplateVersionResponse {
  alertType: string;
  versionId: string;
  number: number;
  subject: string;
  htmlBody: string;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  isActive: boolean;
}

export interface CreateAlertTemplateVersionBody {
  subject: string;
  htmlBody: string;
  notes?: string | null;
}

export interface AlertTemplatePreviewResult {
  alertType: string;
  renderedSubject: string;
  renderedHtmlBody: string;
}

export interface AdminAlertRuleResponse {
  id: string;
  alertType: string;
  senderId: string | null;
  targetingMode: string;
  tenantIds: string[];
  thresholdsPercent: number[];
  sendDayOfWeek: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertAlertRuleBody {
  id?: string | null;
  alertType: string;
  senderId?: string | null;
  targetingMode: string;
  tenantIds?: string[];
  thresholdsPercent?: number[];
  sendDayOfWeek?: string | null;
  isEnabled: boolean;
}

export function listEmailSenders(signal?: AbortSignal): Promise<AdminEmailSenderResponse[]> {
  return adminFetch<AdminEmailSenderResponse[]>(`${BASE}/email/senders`, signal !== undefined ? { signal } : {});
}

export function upsertEmailSender(body: UpsertEmailSenderBody): Promise<AdminEmailSenderResponse> {
  return body.id !== undefined && body.id !== null && body.id !== ''
    ? adminFetch<AdminEmailSenderResponse>(`${BASE}/email/senders/${encodeURIComponent(body.id)}`, { method: 'PUT', json: body })
    : adminFetch<AdminEmailSenderResponse>(`${BASE}/email/senders`, { method: 'POST', json: body });
}

export function deleteEmailSender(id: string): Promise<null> {
  return adminFetch<null>(`${BASE}/email/senders/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getAlertTemplate(alertType: string, signal?: AbortSignal): Promise<AdminAlertTemplateDetail> {
  return adminFetch<AdminAlertTemplateDetail>(`${BASE}/email/templates/${encodeURIComponent(alertType)}`, signal !== undefined ? { signal } : {});
}

export function createAlertTemplateVersion(alertType: string, body: CreateAlertTemplateVersionBody): Promise<AdminAlertTemplateVersionResponse> {
  return adminFetch<AdminAlertTemplateVersionResponse>(`${BASE}/email/templates/${encodeURIComponent(alertType)}/versions`, { method: 'POST', json: body });
}

export function activateAlertTemplateVersion(alertType: string, versionId: string): Promise<null> {
  return adminFetch<null>(`${BASE}/email/templates/${encodeURIComponent(alertType)}/activate/${encodeURIComponent(versionId)}`, { method: 'POST' });
}

export function previewAlertTemplate(alertType: string, body: CreateAlertTemplateVersionBody): Promise<AlertTemplatePreviewResult> {
  return adminFetch<AlertTemplatePreviewResult>(`${BASE}/email/templates/${encodeURIComponent(alertType)}/preview`, { method: 'POST', json: body });
}

export function listAlertRules(signal?: AbortSignal): Promise<AdminAlertRuleResponse[]> {
  return adminFetch<AdminAlertRuleResponse[]>(`${BASE}/email/rules`, signal !== undefined ? { signal } : {});
}

export function upsertAlertRule(body: UpsertAlertRuleBody): Promise<AdminAlertRuleResponse> {
  return body.id !== undefined && body.id !== null && body.id !== ''
    ? adminFetch<AdminAlertRuleResponse>(`${BASE}/email/rules/${encodeURIComponent(body.id)}`, { method: 'PUT', json: body })
    : adminFetch<AdminAlertRuleResponse>(`${BASE}/email/rules`, { method: 'POST', json: body });
}

export function deleteAlertRule(id: string): Promise<null> {
  return adminFetch<null>(`${BASE}/email/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Alert types supported by the admin UI (mirrors the server enum). */
export const ALERT_TYPES: ReadonlyArray<string> = ['NearUsageLimit', 'WeeklyUsageSummary'];

// ---------- Query keys -------------------------------------------------------

export const AdminQueryKeys = {
  emailSenders: () => ['admin', 'email', 'senders'] as const,
  alertTemplate: (alertType: string) => ['admin', 'email', 'templates', alertType] as const,
  alertRules: () => ['admin', 'email', 'rules'] as const,
  tenants: (filter: AdminTenantsFilter) =>
    ['admin', 'tenants', filter.search ?? null, filter.status ?? null, filter.licenseType ?? null, filter.page ?? 1, filter.pageSize ?? 20] as const,
  tenantDetail: (id: string) => ['admin', 'tenants', id] as const,
  tenantRoi: (id: string) => ['admin', 'tenants', id, 'roi'] as const,
  metrics: () => ['admin', 'metrics'] as const,
  operational: () => ['admin', 'metrics', 'operational'] as const,
  billing: () => ['admin', 'metrics', 'billing'] as const,
  techHealth: () => ['admin', 'metrics', 'tech-health'] as const,
  aiUsage: (filter: AiUsageMetricsFilter) =>
    ['admin', 'metrics', 'ai-usage',
      filter.from ?? null, filter.to ?? null,
      filter.tenantId ?? null, filter.providerScope ?? null,
      filter.providerType ?? null, filter.modelName ?? null,
      filter.promptKind ?? null, filter.promptTemplateVersionId ?? null,
      filter.groupBy ?? null,
      filter.topCount ?? null,
    ] as const,
  modelPrices: () => ['admin', 'model-prices'] as const,
  unresolvedModels: (windowDays: number | null) => ['admin', 'ai-usage', 'unresolved-models', windowDays] as const,
  modelPriceHistory: (providerType: ProviderType, modelName: string) =>
    ['admin', 'model-prices', providerType, modelName, 'history'] as const,
  providerModels: (providerId: string) => ['admin', 'platform-providers', providerId, 'models'] as const,
  plans: () => ['admin', 'plans'] as const,
  providers: () => ['admin', 'platform-providers'] as const,
  diResources: () => ['admin', 'di-resources'] as const,
  ocrProviders: () => ['admin', 'ocr-providers'] as const,
  ocrProviderModels: (kind: OcrProviderKind) => ['admin', 'ocr-providers', 'models', kind] as const,
  prompts: () => ['admin', 'prompts'] as const,
  promptDetail: (kind: PromptKind) => ['admin', 'prompts', kind] as const,
  promptVersion: (kind: PromptKind, versionId: string) => ['admin', 'prompts', kind, 'version', versionId] as const,
  preparationEffective: (params: GetEffectivePolicyParams) =>
    ['admin', 'preparation', 'effective', params.tenantId, params.mime, params.digital, params.documentTypeId ?? null] as const,
} as const;
